import { Injectable } from '@angular/core'
import { isNullOrUndefined } from '@gofive/angular-common'
import { Observable, map } from 'rxjs'
import { GetFilterWithCountTypeModel } from 'src/domain/models/base.model'
import { DataTableModel } from 'src/domain/models/datatable.model'
import { SubscriptionTypeModel } from 'src/domain/models/subscription.model'
import { SubscriptionRepository } from 'src/domain/repositories/subscription.repository'
import {
	ActivityTypeEntity,
	DefaultResultData,
	DefaultResultDataSource,
	GetFilterWithCountTypeEntity,
} from '../entities/base.entity'
import {
	HasSubscriptionTypeEntity,
	PlanWithoutUseProductTypeEntity,
	SubscriptionDataTableTypeEntity,
	SubscriptionItemTypeEntity,
	SubscriptionTypeEntity,
} from '../entities/subscription.entity'
import { ActivityMapper } from '../mappers/base/activity.mapper'
import { GetFilterWithCountMapper } from '../mappers/base/get-filter-with-count.mapper'
import { HasSubscriptionMapper } from '../mappers/subscription/has-subscription.mapper'
import { PlanWithoutUseProductMapper } from '../mappers/subscription/plan-without-use-product.mapper'
import { SubscriptionDataTableMapper } from '../mappers/subscription/subscription-data-table.mapper'
import { SubscriptionItemMapper } from '../mappers/subscription/subscription-item.mapper'
import { SubscriptionMapper } from '../mappers/subscription/subscription.mapper'

@Injectable({
	providedIn: 'root',
})
export class SubscriptionImplementationRepository extends SubscriptionRepository {
	subscriptionDataTableMapper = new SubscriptionDataTableMapper()
	getFilterWithCountMapper = new GetFilterWithCountMapper()
	hasSubscriptionMapper = new HasSubscriptionMapper()
	planWithoutUseProduct = new PlanWithoutUseProductMapper()
	activityMapper = new ActivityMapper()
	subscriptionItemMapper = new SubscriptionItemMapper()
	subscriptionMapper = new SubscriptionMapper()

	GetSubscriptionDetail(params: { id: number }) {
		return this.get<DefaultResultData<SubscriptionTypeEntity>>('api/Subscriptions/' + params.id + '/Plans').pipe(
			map((response) => {
				return this.subscriptionMapper.mapFrom(response.data)
			}),
		)
	}

	GetSubscriptionItems(params: { id: number }) {
		return this.get<DefaultResultData<SubscriptionItemTypeEntity[]>>(
			`api/SubscriptionItems/${params.id}/Subscription`,
		).pipe(
			map((response) => {
				return response.data.map((item) => {
					return this.subscriptionItemMapper.mapFrom(item)
				})
			}),
		)
	}

	AddEditSubscription(params: { model: SubscriptionTypeModel }) {
		if (params.model.subscriptionId > 0) {
			return this.put<any>('api/Subscriptions/' + params.model.subscriptionId, params.model)
		} else {
			return this.post<any>('api/Subscriptions', params.model)
		}
	}

	CancelSubscription(params: { subscriptionId: number }) {
		return this.post<any>(`api/Subscriptions/CancelSubscription/${params.subscriptionId}`).pipe(
			map((response) => {
				return response.data
			}),
		)
	}

	DeleteSubscription(params: { subscriptionId: number }) {
		return this.post<any>(`api/Subscriptions/DeleteSubscriptions/${params.subscriptionId}`)
	}

	ReuseSubscription(params: { subscriptionId: number }) {
		return this.post<any>(`api/Subscriptions/ReNewLive/${params.subscriptionId}`)
	}

	Repay(params: { subscriptionId: number }) {
		return this.post<any>(`api/Subscriptions/Repay/${params.subscriptionId}`)
	}

	GetRecentActivites(params: { id: number }) {
		return this.get<DefaultResultData<ActivityTypeEntity[]>>('api/Activities/' + params.id + '/Subscriptions').pipe(
			map((response) => {
				return response.data.map((item) => {
					return this.activityMapper.mapFrom(item)
				})
			}),
		)
	}

	GetDataTableSubscriptions(params: {
		model: DataTableModel
		preCondition: Object
		param?: Array<string>
		search?: string
		filter?: Object
	}) {
		let whereCause: string
		if (params.filter && Object.values(params.filter).filter((x) => !isNullOrUndefined(x)).length > 0) {
			whereCause = this.createWhereCause(params.preCondition, params.param, params.search, params.filter)
		} else if (!isNullOrUndefined(params.search)) {
			whereCause = this.createWhereCause(params.preCondition, params.param, params.search)
		} else {
			whereCause = this.createWhereCause(params.preCondition)
		}
		params.model.whereCause = whereCause
		if (isNullOrUndefined(params.model.orderBy)) {
			params.model.orderBy = 'SubscriptionId'
		}
		return this.post<DefaultResultDataSource<SubscriptionDataTableTypeEntity[]>>(
			'api/Subscriptions/DataTable',
			params.model,
		).pipe(
			map((response) => {
				return response.dataSource.map((item) => {
					return this.subscriptionDataTableMapper.mapFrom(item)
				})
			}),
		)
	}

	UpdateSubscription(params: { subscriptionId: number }) {
		if (params.subscriptionId) {
			return this.post<any>(`api/Subscriptions/UpdateSubscription?SubscriptionId=${params.subscriptionId}`)
		}
	}

	GetPlanWithoutUseProduct(params: { clientId: number }): Observable<any> {
		if (params.clientId) {
			return this.get<DefaultResultData<PlanWithoutUseProductTypeEntity[]>>(
				`api/Subscriptions/GetPlanWithoutUseProduct/${params.clientId}`,
			).pipe(
				map((response) => {
					return response.data.map((item) => {
						return this.planWithoutUseProduct.mapFrom(item)
					})
				}),
			)
		}
	}

	GetIsAddSubscription(params: { clientId: number }): Observable<boolean> {
		if (params.clientId) {
			return this.get<any>(`api/Subscriptions/GetIsAddSubscription/${params.clientId}`)
		}
	}

	GetFilterWithCount(params: {
		model: DataTableModel
		preCondition: Object
		param?: Array<string>
		search?: string
		filter?: Object
	}): Observable<GetFilterWithCountTypeModel[]> {
		let whereCause: string
		if (params.filter && Object.values(params.filter).filter((x) => !isNullOrUndefined(x)).length > 0) {
			whereCause = this.createWhereCause(params.preCondition, params.param, params.search, params.filter)
		} else if (!isNullOrUndefined(params.search)) {
			whereCause = this.createWhereCause(params.preCondition, params.param, params.search)
		} else {
			whereCause = this.createWhereCause(params.preCondition)
		}
		params.model.whereCause = whereCause
		if (isNullOrUndefined(params.model.orderBy)) {
			params.model.orderBy = 'SubscriptionId'
		}
		return this.post<DefaultResultData<GetFilterWithCountTypeEntity[]>>(
			'api/Subscriptions/GetFilterWithCount',
			params.model,
		).pipe(
			map((response) => {
				return response.data.map((item) => {
					return this.getFilterWithCountMapper.mapFrom(item)
				})
			}),
		)
	}

	GetHasSubscription(params: { clientId: string; productId: string }) {
		if (params.clientId && params.productId) {
			return this.post<DefaultResultData<HasSubscriptionTypeEntity>>(
				`api/Subscriptions/v2/HasSubscription/${params.clientId}/product/${params.productId}`,
			).pipe(
				map((response) => {
					return this.hasSubscriptionMapper.mapFrom(response.data)
				}),
			)
		}
	}

	GetCurrentSubscription(params: { productId: string; clientId?: string }) {
		return this.get<any>(`api/Subscriptions/current?productId=${params.productId}&clientId=${params.clientId}`)
	}

	GetPermissionUser() {
		return this.getFromEmpeo<any>('api/UserInfo/GetCurrentUserInfo')
	}
}
